import { ADMIN_ROLE_ID, STAFF_ROLE_ID } from '@allurion/domain';
import { Navigate, useLocation, createBrowserRouter, RouterProvider } from 'react-router-dom';

import { useCurrentUser } from 'src/hooks/useCurrentUser';
import { ClinicPage } from 'src/pages/ClinicPage/ClinicPage';
import ClinicSettingsPage from 'src/pages/ClinicPage/ClinicSettingsPage/ClinicSettingsPage';
import CreatePasswordPage from 'src/pages/CreatePasswordPage';
import { HomePage } from 'src/pages/HomePage';
import InvitePatientPage from 'src/pages/InvitePatientPage/InvitePatientPage';
import LoginPage from 'src/pages/LoginPage';
import ManagePatientsPage from 'src/pages/ManagePatientsPage/ManagePatientsPage';
import NotFoundPage from 'src/pages/NotFoundPage';
import PatientPage from 'src/pages/PatientPage/PatientPage';
import ProfilePage from 'src/pages/ProfilePage';
import ResetPassword from 'src/pages/ResetPasswordPage/ResetPasswordPage';
import TermsAndConditionsPage from 'src/pages/TermsAndConditionsPage';

import { AuthenticatedLayout, Layout } from './components/Layout';
import { ClinicAnalyticsPage } from './pages/ClinicPage/ClinicAnalyticsPage';
import { ClinicPatientsPage } from './pages/ClinicPage/ClinicPatientsPage/ClinicPatientsPage';
import { ClinicProvidersPage } from './pages/ClinicPage/ClinicProvidersPage';
import { EditProviderPage } from './pages/ClinicPage/EditProviderPage';
import { InviteProviderPage } from './pages/ClinicPage/InviteProviderPage/InviteProviderPage';
import { BalloonOrderDetailsPage } from './pages/ClinicPage/OrdersPage/BalloonOrderDetailsPage';
import { ClinicOrdersPage } from './pages/ClinicPage/OrdersPage/ClinicOrdersPage';
import { CreateOrderPage } from './pages/ClinicPage/OrdersPage/CreateOrderPage';
import { ResendInvitationPage } from './pages/ManageUsers/ResendInvitationPage';
import MessageNotificationsPage from './pages/MessageNotificationsPage/MessageNotificationsPage';

declare global {
  interface Window {
    updateTokenInterval: number;
  }
}

export function AppRoutes() {
  const routes = createBrowserRouter([
    {
      path: '/',
      element: <Layout />,
      children: [
        {
          path: '/login',
          element: <LoginPage />,
        },
        {
          path: '/create-password/:clientId/:username/:confirmationCode',
          element: <CreatePasswordPage />,
        },
        {
          path: '/reset-password',
          element: <ResetPassword />,
        },
        {
          path: '/',
          element: (
            <RequireAuth>
              <AuthenticatedLayout />
            </RequireAuth>
          ),
          children: [
            {
              path: '',
              element: <HomePage />,
            },

            {
              path: '/profile',
              element: <ProfilePage />,
            },
            {
              path: '/clinic',
              element: (
                <RequireAuth allowedRoles={[ADMIN_ROLE_ID, STAFF_ROLE_ID]}>
                  <ClinicPage />
                </RequireAuth>
              ),
            },
            {
              path: '/clinic/:clinicId/',
              element: (
                <RequireAuth allowedRoles={[ADMIN_ROLE_ID, STAFF_ROLE_ID]}>
                  <ClinicPage />
                </RequireAuth>
              ),
              children: [
                {
                  path: 'patients',
                  element: (
                    <RequireAuth allowedRoles={[ADMIN_ROLE_ID, STAFF_ROLE_ID]}>
                      <ClinicPatientsPage />
                    </RequireAuth>
                  ),
                },
                {
                  path: 'patient/:patientId',
                  element: (
                    <RequireAuth allowedRoles={[ADMIN_ROLE_ID, STAFF_ROLE_ID]}>
                      <PatientPage />
                    </RequireAuth>
                  ),
                },
                {
                  path: 'invite',
                  element: (
                    <RequireAuth allowedRoles={[ADMIN_ROLE_ID, STAFF_ROLE_ID]}>
                      <InvitePatientPage />
                    </RequireAuth>
                  ),
                },
                {
                  path: 'organize-patients',
                  element: (
                    <RequireAuth allowedRoles={[ADMIN_ROLE_ID]}>
                      <ManagePatientsPage />
                    </RequireAuth>
                  ),
                },
                {
                  path: 'analytics',
                  element: <ClinicAnalyticsPage />,
                },
                {
                  path: 'members',
                  element: (
                    <RequireAuth allowedRoles={[ADMIN_ROLE_ID]}>
                      <ClinicProvidersPage />
                    </RequireAuth>
                  ),
                },
                {
                  path: 'members/resend-invitation',
                  element: (
                    <RequireAuth allowedRoles={[ADMIN_ROLE_ID]}>
                      <ResendInvitationPage />
                    </RequireAuth>
                  ),
                },
                {
                  path: 'members/:providerId',
                  element: (
                    <RequireAuth allowedRoles={[ADMIN_ROLE_ID]}>
                      <EditProviderPage />
                    </RequireAuth>
                  ),
                },
                {
                  path: 'invite-member',
                  element: (
                    <RequireAuth allowedRoles={[ADMIN_ROLE_ID]}>
                      <InviteProviderPage />
                    </RequireAuth>
                  ),
                },
                {
                  path: 'orders/:orderId',
                  element: (
                    <RequireAuth allowedRoles={[ADMIN_ROLE_ID]}>
                      <BalloonOrderDetailsPage />
                    </RequireAuth>
                  ),
                },
                {
                  path: 'orders',
                  element: (
                    <RequireAuth allowedRoles={[ADMIN_ROLE_ID]}>
                      <ClinicOrdersPage />
                    </RequireAuth>
                  ),
                },
                {
                  path: 'new-order',
                  element: (
                    <RequireAuth allowedRoles={[ADMIN_ROLE_ID]}>
                      <CreateOrderPage />
                    </RequireAuth>
                  ),
                },
                {
                  path: `settings`,
                  element: (
                    <RequireAuth allowedRoles={[ADMIN_ROLE_ID]}>
                      <ClinicSettingsPage />
                    </RequireAuth>
                  ),
                },
              ],
            },
            {
              path: '/terms-conditions',
              element: <TermsAndConditionsPage />,
            },
            {
              path: '/message-notifications',
              element: (
                <RequireAuth allowedRoles={[ADMIN_ROLE_ID, STAFF_ROLE_ID]}>
                  <MessageNotificationsPage />
                </RequireAuth>
              ),
            },
            {
              path: '*',
              element: <NotFoundPage />,
            },
          ],
        },
      ],
    },
  ]);

  return <RouterProvider router={routes} />;
}

type RequireAuthProps = {
  children: JSX.Element;
  allowedRoles?: string[];
};

function RequireAuth({ children, allowedRoles = [] }: RequireAuthProps) {
  const { isAuthenticated, userRole } = useCurrentUser();
  const location = useLocation();

  let hasAccess = isAuthenticated;

  if (hasAccess && allowedRoles.length > 0) {
    hasAccess = allowedRoles.some((roleId) => userRole === roleId);
  }

  if (!hasAccess) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    const redirectTo = encodeURIComponent(location.pathname + location.search);

    return <Navigate to={`/login?redirectTo=${redirectTo}`} replace />;
  }

  return children;
}
